/**
 * 管理后台 · 账号管理（见方案 2B.3 A 类）。
 *
 * 表格里只有**账号元数据**：id / 角色 / 用户名 / 昵称 / 邮箱 / 邮箱验证 /
 * TOTP 绑定时间 / 锁定 / 失败次数 / 最后登录 / 创建时间 / 停用时间。
 *
 * 刻意不出现的东西：学生档案、目标、打卡记录、积分明细。
 * 这不是"前端没做"，而是后端 `/admin/users` 就不返回这些字段（见 admin.service.ts）。
 *
 * ⚠️ 这里也**没有**「重置密码」按钮（2026-10 移除）。
 * 用户的判断是"密码忘了应该由家长自己去重置，不应该由管理员去重置 ——
 * 用户多了管理员怎么做得过来"。于是按钮与弹窗、`lib/api/admin.ts` 的调用、
 * 后端那条路由**三处一起删掉**，不留"点了会 404"的死代码。
 * 三种忘记密码的出路见 `lib/queries.ts` 的 `useAdminUserMutations` 注释。
 */
import { useMemo, useState } from 'react';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { AdminShell } from '../../components/layout/AdminShell';
import { ADMIN_ROLE_LABEL, type AdminUserRow } from '../../lib/api/admin';
import { useAdminUserMutations, useAdminUsers } from '../../lib/queries';
import { toErrorMessage } from '../../lib/api/error';
import { formatDateTimeFull } from '../../lib/utils';
import { accountStatusBadges } from '../../lib/adminUserBadges';

const PAGE_SIZE = 20;

/**
 * 「状态」列。整个序列由 `accountStatusBadges()` 一次算完（纯函数、有单测）——
 * 组件只负责把数组画出来，**不在这里自己拼条件**。
 *
 * 这样做的原因是 2026-10 用户报的一个自相矛盾：账号被停用/锁定之后，同一行里
 * 绿色的「正常」与红色徽章同时出现。原来生命周期是由两处条件各画各的
 * （`deletedAt ? 已停用 : 正常` 与独立的 `locked ? 已锁定 : null`），
 * 只要"未停用但被锁定"就会两枚都画出来。现在生命周期三选一（已停用 > 已锁定 > 正常），
 * 而邮箱 / 动态口令 / 失败次数是**另外两个维度**，仍然可以并存。
 *
 * `Date.now()` 在调用点传进去（而不是在纯函数里读），"锁没锁"才测得准。
 */
function StatusBadges({ user }: { user: AdminUserRow }) {
  return (
    <div className="flex flex-wrap gap-1">
      {accountStatusBadges(user, Date.now()).map((badge) => (
        <Badge key={badge.key} tone={badge.tone}>
          {badge.label}
        </Badge>
      ))}
    </div>
  );
}

export function AdminUsersPage() {
  const toast = useToast();
  const [keyword, setKeyword] = useState('');
  const [role, setRole] = useState<'' | AdminUserRow['role']>('');
  const [status, setStatus] = useState<'' | 'active' | 'disabled'>('');
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState<string | null>(null);

  const query = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      ...(keyword.trim() ? { q: keyword.trim() } : {}),
      ...(role ? { role } : {}),
      ...(status ? { status } : {}),
    }),
    [page, keyword, role, status],
  );

  const usersQuery = useAdminUsers(query);
  const { revokeSessions, setStatus: setStatusMutation } = useAdminUserMutations();

  const [confirmDisable, setConfirmDisable] = useState<AdminUserRow | null>(null);

  const users = usersQuery.data?.users ?? [];
  const total = usersQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  /**
   * 有没有生效的筛选条件（空关键字不算筛选 —— 查询参数也只在 trim 后非空时才带上）。
   *
   * 为什么要单独算这个：`/admin/users` 返回的 `total` 是**过滤之后**的条数，
   * 后端不会顺带返回全库总数。所以在筛选状态下直接写「共 3 个账号」会被读成
   * "库里一共只有 3 个账号"（用户 2026-10 就是这么读的：筛选=学生、库里其实是 6 个）。
   * 不筛选时 `total` 就是全库总数，保持原来的「共 N 个账号」不变。
   *
   * ⚠️ 刻意**不为**这句话再加一个请求去取全库总数（那是给一行文案加一次往返）。
   */
  const filtered = keyword.trim() !== '' || role !== '' || status !== '';

  const fail = (err: unknown) => toast.showToast(toErrorMessage(err), 'error');

  const onRevoke = async (user: AdminUserRow) => {
    setBusyId(user.id);
    try {
      const result = await revokeSessions.mutateAsync(user.id);
      toast.showToast(`已强制退出 ${user.username} 的 ${result.revokedSessions} 个设备`, 'success');
    } catch (err) {
      fail(err);
    } finally {
      setBusyId(null);
    }
  };

  const onToggleStatus = async (user: AdminUserRow) => {
    setBusyId(user.id);
    try {
      await setStatusMutation.mutateAsync({ id: user.id, disabled: !user.deletedAt });
      toast.showToast(user.deletedAt ? `已启用 ${user.username}` : `已停用 ${user.username}`, 'success');
    } catch (err) {
      fail(err);
    } finally {
      setBusyId(null);
      setConfirmDisable(null);
    }
  };

  return (
    <AdminShell
      title="账号管理"
      description={
        filtered
          ? `筛选后 ${total} 个账号。这里只有账号元数据，没有学生档案、打卡或积分内容。`
          : `共 ${total} 个账号。这里只有账号元数据，没有学生档案、打卡或积分内容。`
      }
    >
      {/* ---------- 搜索与过滤 ---------- */}
      <Card className="mb-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Input
            label="搜索"
            placeholder="用户名 / 邮箱 / 昵称"
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
              setPage(1);
            }}
          />
          <Select
            label="角色"
            value={role}
            onChange={(e) => {
              setRole(e.target.value as '' | AdminUserRow['role']);
              setPage(1);
            }}
          >
            <option value="">全部角色</option>
            <option value="parent">家长</option>
            <option value="student">学生</option>
            <option value="system_admin">系统管理员</option>
          </Select>
          <Select
            label="状态"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as '' | 'active' | 'disabled');
              setPage(1);
            }}
          >
            <option value="">全部状态</option>
            <option value="active">正常</option>
            <option value="disabled">已停用</option>
          </Select>
        </div>
      </Card>

      {usersQuery.isLoading && <Skeleton className="h-64" />}

      {usersQuery.isError && (
        <Card>
          <p role="alert" className="text-sm text-rose-700">
            读取账号列表失败：{toErrorMessage(usersQuery.error)}
          </p>
        </Card>
      )}

      {!usersQuery.isLoading && users.length === 0 && (
        <EmptyState icon="🔍" title="没有匹配的账号" description="换一个关键字或清空过滤条件试试。" />
      )}

      {users.length > 0 && (
        <Card className="overflow-x-auto p-0">
          <table className="w-full min-w-[56rem] border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">账号</th>
                <th className="px-4 py-3">角色</th>
                <th className="px-4 py-3">状态</th>
                <th className="px-4 py-3">最后登录</th>
                <th className="px-4 py-3">创建时间</th>
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-900">{user.nickname}</p>
                    <p className="text-xs text-slate-500">{user.username}</p>
                    <p className="text-xs text-slate-400">{user.email ?? '（无邮箱）'}</p>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{ADMIN_ROLE_LABEL[user.role]}</td>
                  <td className="px-4 py-3">
                    <StatusBadges user={user} />
                  </td>
                  {/*
                    「最后登录」「创建时间」必须带年份（`formatDateTimeFull` → 'YYYY-MM-DD HH:mm'）：
                    省年份的 '10-16 09:52' 跨年时会被读成今年，账号台账这类信息不能这样。
                    ⚠️ 表格是 `overflow-x-auto` + `min-w-[56rem]`，窄屏**横向滚动**而不是换行，
                    所以多出来的 "2026-" 5 个字符不会把行挤坏。
                  */}
                  <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                    {user.lastLoginAt ? formatDateTimeFull(user.lastLoginAt) : '从未登录'}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                    {formatDateTimeFull(user.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={busyId === user.id && revokeSessions.isPending}
                        onClick={() => void onRevoke(user)}
                      >
                        强制退出
                      </Button>
                      <Button
                        size="sm"
                        variant={user.deletedAt ? 'primary' : 'danger'}
                        loading={busyId === user.id && setStatusMutation.isPending}
                        onClick={() => (user.deletedAt ? void onToggleStatus(user) : setConfirmDisable(user))}
                      >
                        {user.deletedAt ? '启用' : '停用'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* ---------- 分页 ---------- */}
      {total > PAGE_SIZE && (
        <div className="mt-3 flex items-center justify-between text-sm text-slate-600">
          <span>
            第 {page} / {totalPages} 页
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              上一页
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              下一页
            </Button>
          </div>
        </div>
      )}

      {/* ---------- 停用确认 ---------- */}
      <Modal
        open={confirmDisable !== null}
        title="确认停用账号？"
        onClose={() => setConfirmDisable(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmDisable(null)}>
              取消
            </Button>
            <Button
              variant="danger"
              loading={setStatusMutation.isPending}
              onClick={() => confirmDisable && void onToggleStatus(confirmDisable)}
            >
              确认停用
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600">
          停用 <strong>{confirmDisable?.nickname}</strong>（{confirmDisable?.username}）
          会立即吊销它的全部登录会话，并把它标记为已删除（可随时启用恢复）。
        </p>
        <p className="mt-2 text-xs text-slate-500">
          数据不会被删除：学生、打卡与积分记录都保留在数据库里。这条动作会写入审计日志。
        </p>
      </Modal>
    </AdminShell>
  );
}
